// Story 023 — manual-draw state. The Viewer slot mounts a `DrawOverlay`
// when `enabled === true`, captures the draw gesture, and pushes the
// resulting shape as `draft`. The annotation drawer reads `draft` to seed
// its form; on Save / Cancel the drawer clears the draft and (if
// appropriate) toggles `enabled` off.
//
// ANA-R3-GEOM-03 — the draw tool is no longer rectangle-only. The Viewer
// hosts the shared `PolygonEditor` (rect / polygon / polyline modes), so a
// draft now carries the FULL pixel-space `path` (flat `[x0,y0,…]`, top-left
// origin per .vibe/23 Q8) plus its `mode`. The derived `x/y/width/height`
// bbox is kept for the radius prefill + geometry summary (cheap to recompute
// from the path) so the DefectDrawer's bbox code keeps working unchanged
// while submit sends the real polygon `path`.
//
// Bbox coords are in the source image's pixel space (top-left origin per
// .vibe/23 Q8). The Viewer is responsible for mapping
// screen-space → image-space when emitting events.
import { create } from "zustand";

/** The three draw modes, mirroring the shared PolygonEditor's `EditorMode`. */
export type DrawMode = "rect" | "polygon" | "polyline";

export interface DraftShape {
  imageId: string;
  /** Draw mode the shape was created in (rect / polygon / polyline). */
  mode: DrawMode;
  /**
   * The full geometry as a flat PIXEL path `[x0,y0,x1,y1,…]` in the source
   * image's coordinate space. Closed modes (rect/polygon) carry the GeoJSON
   * closing duplicate (first==last); polyline is open. This is what the
   * DefectDrawer sends as `geometry.path`.
   */
  pathPx: number[];
  /** Derived AABB of `pathPx`, kept for the radius prefill + summary. */
  x: number;
  y: number;
  width: number;
  height: number;
}

/** Back-compat alias — older code referred to the rect-only `DraftBox`. */
export type DraftBox = DraftShape;

export interface DrawStoreState {
  enabled: boolean;
  /** Active draw mode for the toolbar segmented control (rect default). */
  mode: DrawMode;
  draft: DraftShape | null;

  setEnabled: (next: boolean) => void;
  toggleEnabled: () => void;
  setMode: (next: DrawMode) => void;
  setDraft: (next: DraftShape | null) => void;
  /** Clear `draft` and disable draw mode. Called by the drawer's Save
   *  on success and by Cancel. */
  reset: () => void;
}

/** AABB of a flat pixel path. */
export function bboxOfPath(pathPx: number[]): {
  x: number;
  y: number;
  width: number;
  height: number;
} {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (let i = 0; i + 1 < pathPx.length; i += 2) {
    const x = pathPx[i];
    const y = pathPx[i + 1];
    if (typeof x !== "number" || typeof y !== "number") continue;
    if (x < minX) minX = x;
    if (y < minY) minY = y;
    if (x > maxX) maxX = x;
    if (y > maxY) maxY = y;
  }
  if (!Number.isFinite(minX)) return { x: 0, y: 0, width: 0, height: 0 };
  return { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
}

/** Centroid (mean of the distinct vertices) of a flat pixel path. Used by the
 *  drawer for chord%/radius location (OQ2 — centroid, not bbox centre). A
 *  closed ring's trailing duplicate is excluded so it doesn't bias the mean. */
export function centroidOfPath(pathPx: number[]): { x: number; y: number } {
  const pts: { x: number; y: number }[] = [];
  for (let i = 0; i + 1 < pathPx.length; i += 2) {
    const x = pathPx[i];
    const y = pathPx[i + 1];
    if (typeof x === "number" && typeof y === "number") pts.push({ x, y });
  }
  if (pts.length === 0) return { x: 0, y: 0 };
  if (pts.length >= 2) {
    const first = pts[0];
    const last = pts[pts.length - 1];
    if (
      first &&
      last &&
      Math.abs(first.x - last.x) < 1e-6 &&
      Math.abs(first.y - last.y) < 1e-6
    ) {
      pts.pop();
    }
  }
  let sx = 0;
  let sy = 0;
  for (const p of pts) {
    sx += p.x;
    sy += p.y;
  }
  return { x: sx / pts.length, y: sy / pts.length };
}

export const useDrawStore = create<DrawStoreState>((set, get) => ({
  enabled: false,
  mode: "rect",
  draft: null,

  setEnabled: (next) => {
    set({ enabled: next, draft: next ? get().draft : null });
  },
  toggleEnabled: () => {
    const next = !get().enabled;
    set({ enabled: next, draft: next ? get().draft : null });
  },
  setMode: (next) => {
    set({ mode: next });
  },
  setDraft: (next) => {
    set({ draft: next });
  },
  reset: () => {
    set({ enabled: false, draft: null });
  },
}));
